import React, { useState } from "react";
import { IResourceComponentsProps } from "@pankod/refine-core";
import { 
  List, 
  Card, 
  Space, 
  Typography, 
  Table, 
  Tag, 
  Button, 
  Popconfirm, 
  Alert, 
  Empty, 
  Spin,
  Checkbox
} from "@pankod/refine-antd";
import { 
  DeleteOutlined, 
  SearchOutlined, 
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined
} from "@ant-design/icons";
import { findDuplicateDrafts, cleanupDuplicateDrafts } from "Services/productServices";
import { format } from "date-fns";
import toast from "react-hot-toast";

const { Text, Title, Paragraph } = Typography;

export const DuplicateCleanup: React.FC<IResourceComponentsProps> = () => {
  const [duplicates, setDuplicates] = useState<any[]>([]);
  const [msDuplicates, setMsDuplicates] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [summary, setSummary] = useState<any>(null);

  const handleScan = async () => {
    setLoading(true);
    setDuplicates([]);
    setMsDuplicates([]);
    setSelectedIds([]);
    setSummary(null);
    try {
      const response = await findDuplicateDrafts();
      if (response.data.success) {
        setDuplicates(response.data.duplicates);
        setMsDuplicates(response.data.meilisearchDuplicates || []);
        setSummary(response.data.summary);
        
        const total = response.data.summary.cmsDuplicateSkuCount + (response.data.meilisearchDuplicates?.length || 0);
        if (total === 0) {
          toast.success("No duplicates found!");
        } else {
          toast.success(`Scan complete: ${total} SKUs need attention`);
        }
      } else {
        toast.error("Failed to scan for duplicates");
      }
    } catch (error) {
      console.error("Duplicate scan failed:", error);
      toast.error("Error connecting to bridge");
    } finally {
      setLoading(false);
    }
  };

  const handleCleanup = async () => {
    if (selectedIds.length === 0) {
      toast.error("No items selected for cleanup");
      return;
    }

    setCleaning(true);
    try {
      const response = await cleanupDuplicateDrafts(selectedIds);
      if (response.data.success) {
        toast.success(`Successfully cleaned up ${response.data.summary.cleanedCount} duplicates`);
        // Re-scan to update the list
        handleScan();
      } else {
        toast.error("Cleanup failed");
      }
    } catch (error) {
      console.error("Cleanup failed:", error);
      toast.error("Error during cleanup operation");
    } finally {
      setCleaning(false);
    }
  };

  const toggleSelection = (id: string) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const selectOlderDuplicates = () => {
    const idsToSelect: string[] = [];
    duplicates.forEach(group => {
      // In each group, keep the most recently updated one (index 0)
      // Select all others for deletion
      group.records.slice(1).forEach((r: any) => idsToSelect.push(r.id));
    });
    setSelectedIds(idsToSelect);
    toast.success(`Selected ${idsToSelect.length} older records for cleanup`);
  };

  const expandedRowRender = (record: any) => {
    const columns = [
      {
        title: "Select",
        key: "select",
        render: (_: any, item: any) => (
          <Checkbox 
            checked={selectedIds.includes(item.id)}
            onChange={() => toggleSelection(item.id)}
          />
        ),
        width: 60
      },
      {
        title: "ID",
        dataIndex: "id",
        key: "id",
        width: 80
      },
      {
        title: "Price",
        dataIndex: "price",
        key: "price",
        render: (val: number) => `$${Number(val).toFixed(2)}`,
        width: 100
      },
      {
        title: "Stock",
        dataIndex: "stock",
        key: "stock",
        render: (val: number) => <Text strong>{val}</Text>,
        width: 80
      },
      {
        title: "Status",
        dataIndex: "published_at",
        key: "published_at",
        render: (val: string) => val ? <Tag color="green">LIVE</Tag> : <Tag color="orange">DRAFT</Tag>,
        width: 100
      },
      {
        title: "Last Updated",
        dataIndex: "updated_at",
        key: "updated_at",
        render: (val: string) => format(new Date(val), "MMM d, HH:mm"),
        width: 150
      },
      {
        title: "Action Strategy",
        key: "strategy",
        render: (_: any, item: any, index: number) => (
          index === 0 
            ? <Text type="success"><CheckCircleOutlined /> Keep (Newest)</Text>
            : <Text type="danger"><ExclamationCircleOutlined /> Duplicate Found</Text>
        )
      }
    ];

    return (
      <Table 
        columns={columns} 
        dataSource={record.records} 
        pagination={false} 
        rowKey="id"
        size="small"
      />
    );
  };

  const mainColumns = [
    {
      title: "Master SKU",
      dataIndex: "masterSku",
      key: "masterSku",
      render: (sku: string) => <Text strong>{sku}</Text>
    },
    {
      title: "Product Name",
      key: "name",
      render: (_: any, record: any) => record.records[0]?.name || "N/A"
    },
    {
      title: "Occurrences",
      key: "count",
      render: (_: any, record: any) => (
        <Badge count={record.records.length} style={{ backgroundColor: '#f5222d' }} />
      )
    },
    {
      title: "Recommendation",
      key: "recommendation",
      render: (_: any, record: any) => {
        const published = record.records.filter((r: any) => r.published_at);
        if (published.length > 1) {
          return <Tag color="error">Multiple Live Records!</Tag>;
        }
        return <Text type="secondary">Review and select older records to delete</Text>;
      }
    }
  ];

  return (
    <List 
      title="Duplicate Product Cleanup"
      headerProps={{
        extra: (
          <Space>
            <Button 
              icon={<SearchOutlined />} 
              onClick={handleScan}
              loading={loading}
              type="primary"
            >
              Scan for Duplicates
            </Button>
            {duplicates.length > 0 && (
              <>
                <Button onClick={selectOlderDuplicates}>
                  Select All Older Versions
                </Button>
                <Popconfirm
                  title={`Are you sure you want to delete ${selectedIds.length} records?`}
                  onConfirm={handleCleanup}
                  okText="Yes, Delete"
                  cancelText="No"
                  okButtonProps={{ danger: true, loading: cleaning }}
                >
                  <Button 
                    danger 
                    icon={<DeleteOutlined />} 
                    disabled={selectedIds.length === 0}
                  >
                    Delete Selected ({selectedIds.length})
                  </Button>
                </Popconfirm>
              </>
            )}
          </Space>
        )
      }}
    >
      <Alert
        message="Resolving Strapi Duplication"
        description={
          <Paragraph>
            During high-concurrency syncs, Strapi might create duplicate records for the same Master SKU. 
            This tool identifies these duplicates and allows you to remove redundant entries while keeping the most recent.
          </Paragraph>
        }
        type="warning"
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Card>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" tip="Scanning CMS database... this may take a moment." />
          </div>
        ) : duplicates.length > 0 ? (
          <Table 
            dataSource={duplicates} 
            columns={mainColumns} 
            expandable={{
              expandedRowRender,
              defaultExpandAllRows: true
            }}
            rowKey="masterSku"
          />
        ) : (
          <Empty 
            description="No duplicates found or scan not yet performed." 
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            <Button type="primary" onClick={handleScan} icon={<SearchOutlined />}>
              Start Scan
            </Button>
          </Empty>
        )}
      </Card>
    </List>
  );
};

// Simple Badge component since we don't have it imported from antd directly in the top list but it's part of antd
const Badge = ({ count, style }: any) => (
  <span style={{ 
    display: 'inline-block', 
    padding: '0 8px', 
    borderRadius: '10px', 
    background: '#ff4d4f', 
    color: '#fff',
    fontSize: '12px',
    lineHeight: '20px',
    ...style 
  }}>
    {count}
  </span>
);
